/**
 * Parameter Heatmap Stability Mock
 *
 * A 2D heatmap showing performance across two parameters
 * (Fast EMA vs Slow EMA). Green = profitable, red = unprofitable.
 * Illustrates broad green zones vs isolated hot spots.
 */

// 8x8 grid: Fast EMA (8-22) vs Slow EMA (20-48)
// Values represent Sharpe ratio: positive = green, negative = red
const grid = [
  // fast\slow:  20    24    28    32    36    40    44    48
  /* 8  */[-0.2, -0.1, 0.3, 0.5, 0.4, 0.2, -0.1, -0.3],
  /* 10 */[0.1, 0.4, 0.8, 1.0, 0.9, 0.6, 0.3, -0.1],
  /* 12 */[0.3, 0.7, 1.1, 1.3, 1.2, 1.0, 0.6, 0.2],
  /* 14 */[0.4, 0.9, 1.2, 1.4, 1.35, 1.1, 0.7, 0.3],
  /* 16 */[0.3, 0.8, 1.15, 1.35, 1.3, 1.05, 0.6, 0.2],
  /* 18 */[0.1, 0.5, 0.9, 1.1, 1.0, 0.8, 0.4, 0.0],
  /* 20 */[-0.1, 0.2, 0.5, 0.7, 0.6, 0.4, 0.1, -0.2],
  /* 22 */[-0.3, -0.1, 0.2, 0.3, 0.2, 0.1, -0.2, -0.4],
];

const fastLabels = [8, 10, 12, 14, 16, 18, 20, 22];
const slowLabels = [20, 24, 28, 32, 36, 40, 44, 48];

function cellColor(sharpe: number): string {
  if (sharpe >= 1.2) return '#22c55e55';
  if (sharpe >= 0.8) return '#22c55e33';
  if (sharpe >= 0.4) return '#22c55e18';
  if (sharpe >= 0) return '#22c55e08';
  if (sharpe >= -0.2) return '#ef444410';
  return '#ef444425';
}

function textColor(sharpe: number): string {
  if (sharpe >= 1.0) return '#22c55e';
  if (sharpe >= 0.4) return '#86efac';
  if (sharpe >= 0) return '#94a3b8';
  return '#ef4444';
}

export default function ParameterHeatmapStabilityMock() {
  // Count profitable cells
  const totalCells = grid.flat().length;
  const profitableCells = grid.flat().filter((v) => v > 0).length;
  const greenPct = Math.round((profitableCells / totalCells) * 100);

  const cols = slowLabels.length;

  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Parameter Heatmap &mdash; Fast EMA vs Slow EMA
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        {/* Column labels */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `28px repeat(${cols}, 1fr)`,
          gap: 2,
          marginBottom: 2,
        }}>
          <div />
          {slowLabels.map((lbl) => (
            <div key={lbl} style={{ textAlign: 'center', fontSize: '0.5rem', color: '#64748b' }}>{lbl}</div>
          ))}
        </div>

        {/* Grid rows */}
        {grid.map((row, i) => (
          <div key={i} style={{
            display: 'grid',
            gridTemplateColumns: `28px repeat(${cols}, 1fr)`,
            gap: 2,
            marginBottom: 2,
          }}>
            {/* Row label */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              paddingRight: 4, fontSize: '0.5rem', color: '#64748b',
            }}>
              {fastLabels[i]}
            </div>
            {/* Cells */}
            {row.map((sharpe, j) => (
              <div key={j} style={{
                aspectRatio: '1',
                background: cellColor(sharpe),
                border: '1px solid #1e293b',
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.5625rem',
                fontWeight: sharpe >= 1.0 ? 700 : 400,
                color: textColor(sharpe),
                fontFamily: 'ui-monospace, monospace',
              }}>
                {sharpe.toFixed(1)}
              </div>
            ))}
          </div>
        ))}

        {/* Axis labels */}
        <div style={{ textAlign: 'center', fontSize: '0.5rem', color: '#64748b', marginTop: 4 }}>
          Slow EMA Period
        </div>
        <div style={{
          position: 'absolute', left: 4, top: '50%',
          transform: 'rotate(-90deg) translateX(-50%)',
          transformOrigin: '0 0',
          fontSize: '0.5rem', color: '#64748b', whiteSpace: 'nowrap',
        }}>
          Fast EMA
        </div>

        {/* Summary */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '0.375rem', fontSize: '0.5625rem' }}>
          <span style={{ color: '#94a3b8' }}>
            Green zone: <strong style={{ color: '#22c55e' }}>{greenPct}%</strong> of combinations profitable
          </span>
          <span style={{ color: '#94a3b8' }}>
            Peak: <strong style={{ color: '#e5e7eb' }}>1.4</strong> at (14, 32)
          </span>
        </div>
      </div>
      <div className="platform-mock-caption">
        A broad green zone across many parameter pairs — the strategy is robust, not peak-dependent.
      </div>
    </div>
  );
}
